import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, Tags, ShieldCheck, ChevronRight, DatabaseBackup } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "./ui";
import { errMsg } from "@/lib/api";
import { downloadBlob } from "@/lib/csv";
import { maskPhone } from "@/lib/format";

const LINKS = [
  { to: "/admin/pix", icon: QrCode, title: "Pix e marca", text: "Chave Pix, código copia e cola, QR Code e logomarca." },
  { to: "/admin/categorias", icon: Tags, title: "Categorias e respostas prontas", text: "Categorias da classificação e textos prontos para responder." },
  { to: "/admin/equipe", icon: ShieldCheck, title: "Acesso ao painel", text: "Nesta versão, somente o Administrador Geral acessa." },
];

const FUTURE = [
  ["Atendimento automático pelo WhatsApp", "Envio direto pela API oficial do WhatsApp Business. Hoje as respostas são enviadas manualmente pelo botão “Responder pelo WhatsApp” (link oficial wa.me), com confirmação manual. Nenhuma automação não oficial é usada."],
  ["Acesso de atendentes", "Perfis com permissões limitadas para outros membros da equipe."],
];

const Backup = () => {
  const { api } = useOutletContext();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      downloadBlob(res.data, `backup_zap_iudp_${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Cópia de segurança baixada.");
    } catch (e) { toast.error("Não foi possível gerar a cópia."); }
    setBusy(false);
  };
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold">Cópia de segurança</h2>
      <p className="text-sm text-muted-foreground">Baixa um arquivo com todos os registros: participantes, mensagens, atendimentos, respostas e configurações. As fotos, os áudios e os documentos continuam no armazenamento seguro. Guarde o arquivo em local protegido, porque ele contém dados pessoais.</p>
      <Button data-testid="backup-download" onClick={run} disabled={busy} variant="outline" className="gap-2"><DatabaseBackup className="h-4 w-4" />{busy ? "Gerando…" : "Baixar cópia de segurança"}</Button>
    </section>
  );
};

const WhatsAppNumbers = () => {
  const { api } = useOutletContext();
  const [rows, setRows] = useState([{ label: "", number: "" }, { label: "", number: "" }]);
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const n = r.data.whatsapp_numbers || [];
      setRows([0, 1].map((i) => ({ label: n[i]?.label || "", number: n[i] ? maskPhone(n[i].number) : "" })));
    });
  }, [api]);
  const set = (i, k) => (e) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: k === "number" ? maskPhone(e.target.value) : e.target.value } : r)));
  const save = async () => {
    try {
      await api.put("/admin/settings/whatsapp", { numbers: rows.filter((r) => r.number.trim()).map((r) => ({ label: r.label || "WhatsApp", number: r.number })) });
      toast.success("Números salvos.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold">Números de WhatsApp Business da igreja</h2>
      <p className="text-sm text-muted-foreground">Cadastro dos dois números que serão conectados na fase de atendimento pelo WhatsApp. Por enquanto ficam apenas registrados; nenhuma mensagem é enviada automaticamente.</p>
      <div className="space-y-2 rounded-lg border border-zap-line bg-white p-4">
        {rows.map((r, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[1fr_200px]">
            <Input data-testid={`wa-label-${i}`} value={r.label} onChange={set(i, "label")} placeholder={`Identificação (ex.: ${i === 0 ? "Secretaria" : "Pastoral"})`} maxLength={40} />
            <Input data-testid={`wa-number-${i}`} value={r.number} onChange={set(i, "number")} placeholder="(11) 91234-5678" inputMode="tel" />
            {r.label && r.number && (
              <p data-testid={`wa-origin-link-${i}`} className="break-all text-xs text-muted-foreground sm:col-span-2">Link para divulgar neste número (registra a origem): {window.location.origin}/?origem=whatsapp-{r.label.toLowerCase().normalize("NFD").replace(/[^a-z0-9]+/g, "-")}</p>
            )}
          </div>
        ))}
        <Button data-testid="wa-save" onClick={save}>Salvar números</Button>
      </div>
    </section>
  );
};

export default function SettingsPage() {
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Configurações" description="Ajustes da Central Digital." />
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="grid max-w-5xl gap-10 px-5 py-6 md:px-8 lg:grid-cols-2">
          <div className="space-y-10">
            <ul className="divide-y divide-zap-line rounded-lg border border-zap-line bg-white">
              {LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`settings-link-${l.to.split("/").pop()}`} className="flex items-center gap-4 px-4 py-4 hover:bg-zap-mist">
                    <l.icon className="h-5 w-5 shrink-0 text-zap-deep" />
                    <span className="min-w-0 flex-1"><span className="block font-semibold">{l.title}</span><span className="block text-sm text-muted-foreground">{l.text}</span></span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
            <WhatsAppNumbers />
            <Backup />
          </div>
          <section className="space-y-3">
            <h2 className="font-display text-lg font-semibold">Em preparação</h2>
            <p className="text-sm text-muted-foreground">Funções planejadas para as próximas fases. Ainda não estão disponíveis.</p>
            <ul data-testid="settings-future" className="divide-y divide-zap-line rounded-lg border border-dashed border-zap-line">
              {FUTURE.map(([t, d]) => (
                <li key={t} className="px-4 py-3"><p className="font-medium">{t} <span className="ml-1 rounded bg-zap-goldsoft px-1.5 py-0.5 text-[11px] font-semibold text-[#7A5A14]">Fase futura</span></p><p className="text-sm text-muted-foreground">{d}</p></li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
